/**
 * Legislation detail sub-tabs that are mostly static per load (screenshots/36–40):
 * Impact report · Timeline · Documents · Lobbying & money · Discussed in meetings.
 * They read the snapshot once; live deltas (document.add, timeline.add) re-render only the list they touch.
 */
import { useMemo, useState } from "react";
import { amountFor, needsOverlapNote, type MoneyView } from "../organizations/logic";
import { ArrowRight, ArrowUpRight, Building, Building2, CalendarClock, CalendarPlus, Check, Columns2, Flag, GitCompare, Hammer,
  HeartHandshake, Home, KeyRound, Loader, Play, Scale, ThumbsUp, Users, Wallet, Film } from "lucide-react";
import { Face } from "../components/shared/Face";
import { findFollow, followsStore, useFollows } from "../stores/followsStore";
import { clock } from "../lib/format";
import { AiChip, CountBadge, Kicker, LinkArrow, SectionHeader, ViewSwitch } from "./ui";
import { useLegislation, type LegislationStore } from "./legislationStore";
import { ComparePanel, DocPreview } from "./Documents";
import type { LegislationDetail } from "./types";
import { NoLobbying } from "../components/shared/States";

const ICONS: Record<string, typeof Users> = {
  "key-round": KeyRound, building: Building, home: Home, "heart-handshake": HeartHandshake, scale: Scale, "building-2": Building2,
  hammer: Hammer, wallet: Wallet, users: Users,
};
const day = (iso: string, year = true) => new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", ...(year ? { year: "numeric" } : {}), timeZone: "UTC" });

/* ---------------- Impact report (36) ---------------- */
export function ImpactReport({ d }: { d: LegislationDetail }) {
  const [level, setLevel] = useState<"plain" | "detailed">("plain");
  const [rated, setRated] = useState<"yes" | "problem" | null>(null);
  const r = d.impact;
  if (!r) return null;
  const rate = (v: "yes" | "problem") => {
    setRated(v);
    void fetch("/api/dashboard/flags", { method: "POST", credentials: "include", headers: { "content-type": "application/json" },
      body: JSON.stringify({ kind: "impact", recordId: d.recordId, accurate: v === "yes" }) }).catch(() => {});
  };
  return (
    <div className="lg-impact">
      <div className="section-head section-head--split">
        <div className="row-gap"><h2>Impact report</h2><AiChip>Written by AI from the official text</AiChip></div>
        <ViewSwitch label="Reading level" value={level} onChange={setLevel} options={[{ id: "plain", label: "Plain English" }, { id: "detailed", label: "Detailed" }]} />
      </div>
      <p className="lg-lede" key={level}>{level === "detailed" && r.detailed ? r.detailed : r.summary}</p>
      {r.who.length > 0 && (
        <section className="lg-stack-12"><Kicker>Who it affects</Kicker>
          <div className="grid-2 equal-cards lg-who">{r.who.map((w) => {
            const Icon = ICONS[w.icon] ?? Users;
            return <article key={w.group} className="who-card"><header><span className="lg-who__icon"><Icon size={14} aria-hidden /></span><b>{w.group}</b></header><p>{w.effect}</p></article>;
          })}</div>
        </section>
      )}
      {r.changes.length > 0 && (
        <section className="lg-stack-12"><Kicker>What changes</Kicker>
          <ol className="numbered">{r.changes.map((c) => <li key={c}>{c}</li>)}</ol>
        </section>
      )}
      {r.money.length > 0 && (
        <section className="lg-stack-12"><Kicker>Money</Kicker>
          <dl className="kv">{r.money.map((m) => (<div key={m.item}><dt>{m.item}</dt><dd>{m.amount}</dd></div>))}</dl>
        </section>
      )}
      {r.when && <section className="note-box lg-when"><Kicker>When</Kicker><p>{r.when}</p></section>}
      <div className="lg-accurate">Was this summary accurate?
        <button className="btn btn--small lg-btn-sm" aria-pressed={rated === "yes"} onClick={() => rate("yes")}>{rated === "yes" ? <Check size={14} aria-hidden /> : <ThumbsUp size={14} aria-hidden />}Yes</button>
        <button className="btn btn--small lg-btn-sm" aria-pressed={rated === "problem"} onClick={() => rate("problem")}><Flag size={14} aria-hidden />Report a problem</button>
      </div>
    </div>
  );
}

/* ---------------- Timeline (37) ---------------- */
export function Timeline({ d, focusDate, onCompare, onProjected }: { d: LegislationDetail; focusDate?: string; onCompare: () => void; onProjected: () => void }) {
  const [wording, setWording] = useState<"plain" | "official">("plain");
  const current = d.timeline.find((e) => !e.upcoming);
  return (
    <div className="lg-timeline-tab">
      <SectionHeader title="Timeline" count={`${d.timeline.length} steps`}>
        <span className="spacer" />
        <ViewSwitch label="Wording" value={wording} onChange={setWording} options={[{ id: "plain", label: "Plain English" }, { id: "official", label: "Official wording" }]} />
      </SectionHeader>
      <ol className="timeline-v">
        {d.timeline.map((e) => (
          // L8: arriving from a stepper click flashes the entry once
          <li key={e.date + e.action} id={`tl-${e.date}`} data-state={e.upcoming ? "next" : e === current && d.stage !== "final" && d.stage === e.stage ? "now" : undefined}
            data-flash={focusDate === e.date || undefined}>
            <time>{day(e.date)}</time>
            
            <div className="lg-tl__body">
              <header><b>{e.action}</b><span className="chip chip--neutral lg-chip-12">{e.body}</span>{e.upcoming && <span className="chip chip--warning lg-chip-12">Upcoming</span>}</header>
              <p>{wording === "official" && e.official ? e.official : e.plain}</p>
              {wording === "plain" && e.official && <blockquote>Official: {e.official}</blockquote>}
              {e.upcoming && d.forecast && <LinkArrow onClick={onProjected}>Projected {d.forecast.projectedYes}–{d.forecast.projectedNo}</LinkArrow>}
              {e.watch && <a className="ts-link lg-watch" href={`/dashboard/meetings/${encodeURIComponent(e.watch.meetingId)}?t=${clock(e.watch.at)}`}><Play size={12} aria-hidden />Watch at {clock(e.watch.at)}</a>}
              {e.substituted && d.documents.filter((x) => x.version).length > 1 && <LinkArrow onClick={onCompare} icon={<GitCompare size={14} aria-hidden />}>Compare versions</LinkArrow>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ---------------- Documents (38) ---------------- */
export function Documents({ d, compare, setCompare }: { d: LegislationDetail; compare: boolean; setCompare: (v: boolean) => void }) {
  const [sel, setSel] = useState(() => d.documents.find((x) => x.version?.current) ?? d.documents[0]);
  const versions = useMemo(() => d.documents.filter((x) => x.version).sort((a, b) => a.version!.date.localeCompare(b.version!.date)), [d.documents]);
  const kindOf = (x: LegislationDetail["documents"][number]) => x.version ? `${x.kind} · ${x.version.label.toLowerCase()}${x.version.current ? " · current" : ""}` : x.kind;
  return (
    <div className="lg-docs">
      {versions.length > 1 && (
        <div className="versions">
          <GitCompare size={16} aria-hidden /><b>{versions.length} versions of the {d.type.toLowerCase()}</b>
          {versions.map((v, i) => (<span key={v.name} className="lg-versions__item">
            {i > 0 && <ArrowRight size={14} aria-hidden />}
            <button className="version-pill" data-current={v.version!.current || undefined} onClick={() => setSel(v)}>{v.version!.label} · {day(v.version!.date, false)}{v.version!.current ? " (current)" : ""}</button>
          </span>))}
          <span className="spacer" />
          <button className="btn" onClick={() => setCompare(true)}><Columns2 size={14} aria-hidden />Compare side by side</button>
        </div>
      )}
      {compare && versions.length > 1 ? <ComparePanel a={versions[0]} b={versions[versions.length - 1]} onClose={() => setCompare(false)} /> : (
        <div className="doc-pane">
          <ul className="doc-list">
            {d.documents.map((x) => (
              <li key={x.name}><button aria-current={x === sel || undefined} onClick={() => setSel(x)}>
                <span className="pdf-icon">PDF</span><span><b>{x.name}</b><small>{kindOf(x)}</small></span>
              </button></li>
            ))}
            <li className="lg-doclist__note">Originals on the City Clerk's site</li>
          </ul>
          {sel && <DocPreview doc={sel} />}
        </div>
      )}
    </div>
  );
}

/* ---------------- Lobbying & money (39) ---------------- */
export function Lobbying({ store }: { store: LegislationStore }) {
  const client = useLegislation(store, (s) => s.detail?.lobbying.clients.find((c) => c.id === s.clientId));
  const rows = useLegislation(store, (s) => s.detail?.lobbying.influence[s.clientId ?? ""]);
  const hasFiledClients = useLegislation(store, (s) => !!s.detail?.lobbying.clients.some((c) => !c.isTeam));
  const item = useLegislation(store, (s) => s.detail);
  const [range, setRange] = useState<"since" | "year">("since");
  const [view, setView] = useState<MoneyView>("all");
  // Totals by source: "Company only" drops lobbyists' own giving (organizations/logic amountFor).
  const amt = (r: NonNullable<typeof rows>[number], which: "since" | "year") =>
    r.money ? amountFor(r.money[which], view) : which === "since" ? r.sinceAmount : r.pastYearAmount;
  const hasLobbyistMoney = (rows ?? []).some((r) => r.money && needsOverlapNote(r.money[range], view));
  const top = useMemo(() => [...(rows ?? [])]
    .sort((a, b) => amt(b, range) - amt(a, range))
    .filter((r) => amt(r, range) > 0).slice(0, 10), [rows, range, view]); // eslint-disable-line react-hooks/exhaustive-deps
  // Never hide the section (screenshots/54 state 2): no client has filed on this item.
  if (!client || client.isTeam && !hasFiledClients) return <NoLobbying onAlert={() => { if (item) alertOnFiling(item); }} />;
  const max = Math.max(1, ...top.map((r) => amt(r, range)));
  const abbr = client.name.split(/\s+/).map((w) => w[0]).join("");
  const usd = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`;
  return (
    <div className="lg-lobbying">
      <article className="client-card">
        <header>
          <span className="logo-tile"><Building2 size={18} aria-hidden /></span>
          <span className="lg-clientcard__name">{client.slug ? <a className="lg-org-link" href={`/dashboard/organizations/${encodeURIComponent(client.slug)}`}><b>{client.name}</b></a> : <b>{client.name}</b>}<small>Lobbying client · {client.kind}</small></span>
          <span className={`chip chip--${client.positionBasis === "unclear" ? "neutral" : client.position === "oppose" ? "negative" : "positive"} lg-chip-13`}>
            {client.positionBasis === "unclear" ? "Position unclear" : `${client.position === "oppose" ? "Opposes" : "Supports"} this bill${client.positionBasis === "inferred" ? " · inferred" : ""}`}
          </span>
          {client.slug && <a className="btn" href={`/dashboard/organizations/${encodeURIComponent(client.slug)}`}><ArrowUpRight size={14} aria-hidden />Organization profile</a>}
        </header>
        <p className="lg-clientcard__about">{client.summary}</p>
        {client.asks.length > 0 && (
          <section className="lg-stack-10"><div className="row-gap"><Kicker>What they lobby for</Kicker><AiChip>From lobbying filings</AiChip></div>
            <div className="lg-asks">{client.asks.map((a) => <div key={a.title} className="ask"><b>{a.title}</b><p>{a.detail}</p></div>)}</div>
          </section>
        )}
        {client.gain && <section className="lg-stack-6"><Kicker>What they'd gain</Kicker><p className="lg-gain">{client.gain}</p></section>}
      </article>
      <section className="lg-stack-14">
        <div className="section-head section-head--split">
          <div className="row-gap"><h2>{abbr} contributions to members {range === "since" ? "since 2019" : "in the past year"}</h2><CountBadge>Top 10</CountBadge></div>
          <ViewSwitch label="Range" value={range} onChange={setRange} options={[{ id: "since", label: "Since 2019" }, { id: "year", label: "Past year" }]} />
        </div>
        <div className="hbars" role="list">
          {top.map((r) => {
            const v = amt(r, range);
            return (
              <div key={r.personId} className="hbars__row" role="listitem">
                <a href={`/dashboard/people/${encodeURIComponent(r.personId)}/money`}>{r.name}</a><span className="lg-hbars__ward">Ward {r.ward}</span>
                <span className="hbars__track"><span style={{ width: `${(v / max) * 100}%` }} /></span>
                <b>{usd(v)}</b>
                <span className="lg-hbars__other">{range === "since" ? `${amt(r, "year") ? usd(amt(r, "year")) : "—"} past yr` : `${usd(amt(r, "since"))} since 2019`}</span>
              </div>
            );
          })}
        </div>
        {(rows ?? []).some((r) => r.money?.since.lobbyists) && (
          <div className="lg-moneyview"><ViewSwitch label="Money view" value={view} onChange={setView} options={[{ id: "all", label: "All sources" }, { id: "company", label: "Company only" }]} /></div>
        )}
        <p className="caveat">Money near a vote is not evidence that it influenced the vote. Contributions are from Illinois State Board of Elections and Board of Ethics filings, matched to the client by name.
          {hasLobbyistMoney && " Lobbyists' own contributions count under every client they represent. Switch to \"Company only\" to see what the organization itself gave."}</p>
      </section>
    </div>
  );
}

/* ---------------- Discussed in meetings (40) ---------------- */
export function DiscussedIn({ d }: { d: LegislationDetail }) {
  // Live alert = following this record (the live-now banner fires for followed items).
  const followed = useFollows((st) => findFollow(st.follows, "legislation", d.recordId));
  return (
    <div className="lg-discussed">
      {d.nextMeeting && (
        <div className="next-meeting">
          <span className="lg-next__icon"><CalendarClock size={16} aria-hidden /></span>
          <span className="lg-next__text"><b>Next: {d.nextMeeting.title} · {d.nextMeeting.at}</b><small>We'll alert you the moment it's called, and link the live transcript.</small></span>
          <label className="lg-next__alert">Live alert<button className="switch lg-switch-lg" role="switch" aria-checked={!!followed} aria-label="Live alert" onClick={() => {
            if (followed) void followsStore.getState().unfollow(followed.id);
            else void followsStore.getState().follow({ type: "legislation", refId: d.recordId, label: d.title });
          }} /></label>
          <a className="btn" href={`/api/dashboard/legislation/${encodeURIComponent(d.recordId)}/next.ics`}><CalendarPlus size={14} aria-hidden />Calendar</a>
        </div>
      )}
      {d.meetings.map((m) => (
        <article key={m.meetingId} className="meeting-card">
          <header>
            <span className="lg-frame">
              {m.frameUrl ? <img src={m.frameUrl} alt="" width={132} height={74} /> : <span className="lg-frame__empty" />}
              {m.frameLabel && <span className="lg-frame__label"><Film size={10} aria-hidden />{m.frameLabel}</span>}
            </span>
            <span className="lg-mcard__what"><Kicker>{m.date}</Kicker><b>{m.title}</b><small>{m.action}</small></span>
            <span className="meeting-card__right">
              {m.transcript === "ready" ? <span className="status status--ready"><Check size={14} aria-hidden />Transcript</span>
                : <span className="status status--processing"><Loader size={14} aria-hidden />Transcribing…</span>}
              <a className="btn btn--small lg-btn-sm" href={`/dashboard/meetings/${encodeURIComponent(m.meetingId)}`}><Play size={14} aria-hidden />Open meeting</a>
            </span>
          </header>
          {m.moments.map((x) => (
            <div key={x.personId + x.at} className="moment">
              <Face name={x.name} photoUrl={x.photoUrl} size={30} />
              <span><span className="lg-moment__head"><b>{x.name}</b><a className="ts-link lg-watch" href={`/dashboard/meetings/${encodeURIComponent(m.meetingId)}?t=${clock(x.at)}`}><Play size={12} aria-hidden />{clock(x.at)}</a></span><p>{x.summary}</p></span>
            </div>
          ))}
          <p className="lg-mcard__foot">{m.moments.length ? "AI summary of the moment. Watch the clip for the exact words." : "Appears when the transcript is ready"}</p>
        </article>
      ))}
    </div>
  );
}

/** "Alert me if someone files" (NoLobbying): follow the item, so a filing match notifies (followsStore toasts with Undo). */
export function alertOnFiling(d: Pick<LegislationDetail, "recordId" | "title">) {
  if (findFollow(followsStore.getState().follows, "legislation", d.recordId)) return;
  void followsStore.getState().follow({ type: "legislation", refId: d.recordId, label: d.title });
}
